/**
 * SupportsTab — section "Supports" de la page Réglages.
 *
 * CRUD complet sur le catalogue `supports` (papier fine art, toile, dibond,
 * acrylique…). Plus simple que `formats`/`framings` — pas de surcharge prix
 * ni de couleur, juste libellés + finition.
 */

import { useState } from 'react'
import { Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import KebabMenu from '@/components/ui/KebabMenu'
import Modal from '@/components/ui/Modal'
import { FormField } from '@/components/forms/FormField'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import {
  useCreateSupport,
  useDeleteSupport,
  useSupports,
  useUpdateSupport,
} from '@/hooks/useCatalog'
import {
  supportFormSchema,
  type SupportFormParsed,
  type SupportFormValues,
} from '@/schemas/catalog'
import { type SupportResponse } from '@/types/catalog'

export default function SupportsTab() {
  const { confirm } = useConfirm()
  const supportsQuery = useSupports()
  const createMutation = useCreateSupport()
  const updateMutation = useUpdateSupport()
  const deleteMutation = useDeleteSupport()

  const [editing, setEditing] = useState<SupportResponse | null>(null)
  const [creating, setCreating] = useState(false)

  const handleToggleActive = (row: SupportResponse) => {
    updateMutation.mutate(
      { id: row.id, payload: { active: !row.active } },
      {
        onSuccess: () => {
          toast.success(row.active ? `« ${row.label_fr} » désactivé` : `« ${row.label_fr} » activé`)
        },
        onError: (err) => {
          toast.error(err.detail ?? 'Mise à jour impossible')
        },
      },
    )
  }

  const handleDelete = async (row: SupportResponse) => {
    const ok = await confirm({
      title: 'Supprimer ce support ?',
      message: `« ${row.label_fr} » sera retiré du catalogue. La suppression échoue si une variante l'utilise — désactive plutôt l'entrée.`,
      variant: 'danger',
      confirmText: 'Supprimer',
    })
    if (!ok) return
    deleteMutation.mutate(row.id, {
      onError: (err) => {
        toast.error(err.detail ?? 'Suppression impossible')
      },
      onSuccess: () => {
        toast.success(`Support « ${row.label_fr} » supprimé`)
      },
    })
  }

  const rows = supportsQuery.data ?? []
  const isLoading = supportsQuery.isLoading

  return (
    <section className="settings-section">
      <header className="settings-section-header">
        <div>
          <h2 className="ui-h4 settings-section-title">Supports</h2>
          <p className="ui-body ui-text-muted settings-section-subtitle">
            Substrats d'impression — papier fine art, toile, dibond aluminium, plexiglas, etc.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" size={16} />
          Ajouter un support
        </Button>
      </header>

      {!isLoading && rows.length === 0 ? (
        <EmptyState
          title="Aucun support dans ton catalogue"
          description="Ajoute « Papier fine art mat », « Toile tendue », « Dibond aluminium »… selon ce que ton atelier propose."
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>
              Ajouter le premier support
            </Button>
          }
        />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Libellé</th>
                <th>Finition</th>
                <th>Ordre</th>
                <th>Statut</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-inactive={!row.active || undefined}>
                  <td>
                    <strong>{row.label_fr}</strong>
                    {row.label_en ? (
                      <span className="settings-table-secondary">{row.label_en}</span>
                    ) : null}
                  </td>
                  <td>{row.finish ?? '—'}</td>
                  <td className="settings-table-numeric">{row.display_order}</td>
                  <td>
                    {row.active ? (
                      <Badge variant="success">Actif</Badge>
                    ) : (
                      <Badge variant="default">Désactivé</Badge>
                    )}
                  </td>
                  <td className="settings-table-actions">
                    <KebabMenu
                      ariaLabel={`Actions sur ${row.label_fr}`}
                      items={[
                        {
                          id: 'edit',
                          label: 'Modifier',
                          icon: Pencil,
                          onSelect: () => setEditing(row),
                        },
                        {
                          id: 'toggle',
                          label: row.active ? 'Désactiver' : 'Activer',
                          icon: row.active ? PowerOff : Power,
                          onSelect: () => handleToggleActive(row),
                        },
                        {
                          id: 'delete',
                          label: 'Supprimer',
                          icon: Trash2,
                          variant: 'danger',
                          onSelect: () => void handleDelete(row),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating ? (
        <SupportModal
          mode="create"
          onClose={() => setCreating(false)}
          onSubmit={(values) =>
            new Promise<void>((resolve, reject) => {
              createMutation.mutate(values, {
                onSuccess: () => {
                  toast.success(`Support « ${values.label_fr} » ajouté`)
                  setCreating(false)
                  resolve()
                },
                onError: (err) => {
                  toast.error(err.detail ?? 'Création impossible')
                  reject(err)
                },
              })
            })
          }
        />
      ) : null}

      {editing ? (
        <SupportModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(values) =>
            new Promise<void>((resolve, reject) => {
              updateMutation.mutate(
                { id: editing.id, payload: values },
                {
                  onSuccess: () => {
                    toast.success(`Support « ${values.label_fr} » mis à jour`)
                    setEditing(null)
                    resolve()
                  },
                  onError: (err) => {
                    toast.error(err.detail ?? 'Mise à jour impossible')
                    reject(err)
                  },
                },
              )
            })
          }
        />
      ) : null}
    </section>
  )
}

// ─── Modal create/edit ──────────────────────────────────────────────────

interface SupportModalProps {
  mode: 'create' | 'edit'
  initial?: SupportResponse
  onClose: () => void
  onSubmit: (values: SupportFormParsed) => Promise<void>
}

function SupportModal({ mode, initial, onClose, onSubmit }: SupportModalProps) {
  const form = useForm<SupportFormValues, unknown, SupportFormParsed>({
    resolver: zodResolver(supportFormSchema),
    defaultValues: {
      label_fr: initial?.label_fr ?? '',
      label_en: initial?.label_en ?? '',
      finish: initial?.finish ?? '',
      display_order: initial?.display_order ?? 0,
    },
  })

  const submitting = form.formState.isSubmitting

  const handleValid = async (values: SupportFormParsed) => {
    await onSubmit(values)
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'Nouveau support' : `Modifier ${initial?.label_fr}`}
      size="form"
      kicker="Catalogue · Supports"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleValid)} noValidate>
          <Modal.Body className="settings-form-grid">
            <FormField
              name="label_fr"
              label="Libellé"
              required
              placeholder="Papier fine art mat"
              focusOnMount
            />
            <FormField
              name="label_en"
              label="Libellé EN (optionnel)"
              placeholder="Matte fine art paper"
            />
            <div className="settings-form-row">
              <FormField
                name="finish"
                label="Finition (optionnel)"
                placeholder="Mat, satiné, brillant…"
              />
              <FormField name="display_order" label="Ordre d'affichage" type="number" min={0} />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" loading={submitting}>
              {mode === 'create' ? 'Ajouter' : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}
